import { z } from "zod";
import { GastoCajaRespuestaSchema } from "./caja.schema.js";
export const FiltroReportesSchema = z.object({
    desde: z.string().optional(),
    hasta: z.string().optional(),
});
// ------------------------------------------------------------------
// Respuestas (lo que el backend REALMENTE devuelve). z.coerce.number()
// en los montos por lo mismo de siempre: Prisma serializa `Decimal` como
// string (ver PROGRESS.md sección 5b/7).
// ------------------------------------------------------------------
const MontoCoercible = z.coerce.number();
export const MedioPagoReporteSchema = z.object({
    medio: z.string(),
    etiqueta: z.string(),
    total: MontoCoercible,
    porcentaje: MontoCoercible,
});
export const VentaPorHoraReporteSchema = z.object({
    horaLabel: z.string(),
    total: MontoCoercible,
    facturas: MontoCoercible,
    porcentaje: MontoCoercible,
});
export const ResumenVentasReporteSchema = z.object({
    ventasTotales: MontoCoercible,
    totalFacturas: MontoCoercible,
    ticketPromedio: MontoCoercible,
    totalClientes: MontoCoercible,
    totalDescuentos: MontoCoercible,
    mediosPago: z.array(MedioPagoReporteSchema),
    ventasPorHora: z.array(VentaPorHoraReporteSchema).optional(),
    // Egresos de caja (ver GastoCaja) del mismo período del reporte -- para que
    // el admin vea de una las ventas Y los gastos del día sin ir a otra pantalla.
    totalGastos: MontoCoercible,
    gastos: z.array(GastoCajaRespuestaSchema),
});
export const DesempenoMeseroReporteSchema = z.object({
    usuarioId: z.string(),
    nombre: z.string(),
    rol: z.string(),
    totalVentas: MontoCoercible,
    pedidosTomados: MontoCoercible,
    mesasAtendidas: MontoCoercible,
    ticketPromedio: MontoCoercible,
});
export const TopProductoReporteSchema = z.object({
    productoId: z.string(),
    nombre: z.string(),
    categoria: z.string(),
    cantidadVendida: MontoCoercible,
    totalRecaudado: MontoCoercible,
});
export const MetricasCocinaReporteSchema = z.object({
    totalComandasImpresas: MontoCoercible,
    pedidosPendientes: MontoCoercible,
    pedidosDemorados: MontoCoercible,
    tiempoPromedioMinutos: MontoCoercible,
});
export const PedidoCanceladoReporteSchema = z.object({
    id: z.string(),
    mesaNumero: z.number().nullable().optional(),
    mesaNombre: z.string().nullable().optional(),
    total: MontoCoercible,
    motivoCancelacion: z.string().nullable().optional(),
    creadoEn: z.string(),
    canceladoEn: z.string().nullable().optional(),
    creadoPor: z.string().nullable().optional(),
    canceladoPor: z.string().nullable().optional(),
    items: z.array(z.object({ cantidad: z.number(), nombre: z.string() })),
});
export const VentaRecienteSchema = z.object({
    id: z.string(),
    creadoEn: z.string(),
    total: MontoCoercible,
    mesaNumero: z.number().nullable(),
    mesaNombre: z.string().nullable(),
    cajeroNombre: z.string(),
    // Quién atendió la mesa (la mesera de esa sesión), distinto de quién cobró
    // (cajeroNombre) -- null en ventas sin mesa (venta rápida de mostrador).
    meseraNombre: z.string().nullable(),
    mediosPago: z.array(z.string()),
});
// Paginado -- antes el listado de ventas tenía un tope duro de 100 (el
// selector "Ver: 100 ventas") y no había forma de ver las anteriores a eso
// en un corte de caja con muchas facturas. `total` es cuántas facturas hay
// en total para ese filtro (sin el `take` de la página), para poder armar
// "página X de Y" y deshabilitar "Siguiente" cuando ya no hay más.
export const VentasRecientesRespuestaSchema = z.object({
    ventas: z.array(VentaRecienteSchema),
    total: z.number(),
});
export const FacturaItemDetalleSchema = z.object({
    id: z.string(),
    nombreProducto: z.string(),
    cantidad: z.number(),
    precioUnitario: MontoCoercible,
    subtotal: MontoCoercible,
});
export const PagoDetalleVentaSchema = z.object({
    id: z.string(),
    medioPago: z.string(),
    monto: MontoCoercible,
    montoRecibido: MontoCoercible.nullable(),
    cambio: MontoCoercible.nullable(),
    referencia: z.string().nullable(),
});
export const ItemMesaOcupadaSchema = z.object({
    nombre: z.string(),
    cantidad: z.number(),
    precioUnitario: MontoCoercible,
    subtotal: MontoCoercible,
});
export const MesaOcupadaInfoSchema = z.object({
    estaOcupada: z.boolean(),
    mesaId: z.string(),
    mesaNumero: z.number(),
    meseraNombre: z.string().nullable(),
    personas: z.number().nullable(),
    abiertaEn: z.string().nullable(),
    totalActual: MontoCoercible,
    itemsActuales: z.array(ItemMesaOcupadaSchema),
});
/** Detalle completo de una venta ya facturada, para el modal "Últimas Ventas". */
export const VentaDetalleSchema = z.object({
    id: z.string(),
    creadoEn: z.string(),
    mesaNumero: z.number().nullable(),
    mesaNombre: z.string().nullable(),
    cajeroNombre: z.string(),
    meseraNombre: z.string().nullable(),
    clienteNombre: z.string().nullable(),
    clienteDocumento: z.string().nullable(),
    subtotal: MontoCoercible,
    descuento: MontoCoercible,
    motivoDescuento: z.string().nullable(),
    propina: MontoCoercible,
    total: MontoCoercible,
    items: z.array(FacturaItemDetalleSchema),
    pagos: z.array(PagoDetalleVentaSchema),
    mesaOcupadaInfo: MesaOcupadaInfoSchema.nullable().optional(),
});
// ------------------------------------------------------------------
// Reporte de Productos Vendidos por Corte de Caja y Desglose por Mesas
// ------------------------------------------------------------------
export const VentaProductoDetalleMesaSchema = z.object({
    facturaId: z.string(),
    fechaHora: z.string(),
    mesaNumero: z.number().nullable(),
    mesaNombre: z.string().nullable(),
    esVentaRapida: z.boolean(),
    cantidad: z.number(),
    precioUnitario: MontoCoercible,
    subtotal: MontoCoercible,
    cajeroNombre: z.string(),
    meseraNombre: z.string().nullable(),
    clienteNombre: z.string().nullable(),
});
export const ResumenMesaProductoSchema = z.object({
    mesaTexto: z.string(),
    cantidad: z.number(),
});
export const ProductoVendidoReporteSchema = z.object({
    productoId: z.string().nullable(),
    nombre: z.string(),
    categoria: z.string(),
    precioPromedio: MontoCoercible,
    cantidadTotal: z.number(),
    totalRecaudado: MontoCoercible,
    ventas: z.array(VentaProductoDetalleMesaSchema),
    resumenMesas: z.array(ResumenMesaProductoSchema),
});
export const InfoTurnoReporteSchema = z.object({
    id: z.string(),
    cajeroNombre: z.string(),
    abiertoEn: z.string(),
    cerradoEn: z.string().nullable(),
    estado: z.string(),
    montoInicial: MontoCoercible,
    totalVentas: MontoCoercible,
    totalFacturas: z.number(),
});
export const ProductosVendidosRespuestaSchema = z.object({
    productos: z.array(ProductoVendidoReporteSchema),
    totalProductosVendidos: z.number(),
    totalRecaudadoGeneral: MontoCoercible,
    turnoSeleccionado: InfoTurnoReporteSchema.nullable(),
    turnosDisponibles: z.array(InfoTurnoReporteSchema),
});
//# sourceMappingURL=reportes.schema.js.map